import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(timezone);
dayjs.extend(utc);

/**
 * formatDateTimeToLabel is a function that formats the date time to a label using local timezone.
 * It is used to display the date time in the build hub.
 *
 * @param dateTime - The date time to format.
 * @returns The formatted date time label.
 * @example
 * formatDateTimeToLabel('2024-01-01T00:00:00Z') // returns 'Monday, 1 January 2024 at 12:00 AM'
 * formatDateTimeToLabel(null) // returns '-'
 */
export const formatDateTimeToLabel = (dateTime: string | null) => {
    if (!dateTime) {
        return '-';
    }

    // Use local timezone for formatting
    const localTimezone = dayjs.tz.guess();
    const localDate = dayjs(dateTime).tz(localTimezone);

    const date = localDate.format('dddd, D MMM YYYY');
    const time = localDate.format('h:mm A');

    return `${date} at ${time}`;
};

/**
 * formatFileSize is a function that formats the file size from bytes to a human readable format.
 * It is used to display the file size in the build hub.
 *
 * @param size - The size to format.
 * @returns The formatted file size.
 * @example
 * formatFileSize(1024) // returns '1 KB'
 * formatFileSize(1024 * 1024) // returns '1 MB'
 * formatFileSize(1024 * 1024 * 1024) // returns '1 GB'
 */
export const formatFileSize = (size: number) => {
    if (size < 1024) {
        return `${size} bytes`;
    }

    if (size < 1024 * 1024) {
        return `${(size / 1024).toFixed(0)} KB`;
    }

    if (size < 1024 * 1024 * 1024) {
        return `${(size / 1024 / 1024).toFixed(2)} MB`;
    }

    return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const formatFileType = (type: string) => {
    return type.split('/')[1];
};
